import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Play, Search, ChevronLeft, ChevronRight, Lock, Wrench, Mic, Star, BookOpen, Volume2, Loader2,
} from 'lucide-react';
import Navbar from './Navbar';
import { API_BASE } from '../config/api';

// Bốn nhánh của Speaking. Tên tiếng Anh giữ nguyên theo feedback để đồng nhất với ba kỹ
// năng kia; phần mô tả bên trong mới là tiếng Việt.
const SECTIONS = [
    { key: 'mock', label: 'Mock Test', icon: Mic,
      blurb: 'Thi thử với giám khảo AI' },
    { key: 'forecast', label: 'Forecast Topics', icon: Star,
      blurb: 'Luyện theo chủ đề dự đoán' },
    // Nhánh thứ tư, thêm 09/09: bài học phát âm — lý thuyết admin viết + bộ luyện tập AI
    // sinh theo đúng bài (xem exam_elements/speaking/PronunciationLessons.js).
    { key: 'lessons', label: 'Pronunciation Lessons', icon: Volume2,
      blurb: 'Học phát âm theo bài' },
    { key: 'documents', label: 'Documents', icon: BookOpen,
      blurb: 'Tra cứu tài liệu' },
];
const toAbsoluteUrl = (u) => (u && u.startsWith('/')) ? `${API_BASE}${u}` : u;

// Cột sóng ở thẻ giới thiệu + nhãn "chức năng mới" cạnh tiêu đề.
// Nhãn là một cái thẻ treo: mũi nhọn chọc vào chữ cuối của tiêu đề, hơi nghiêng lên, đung
// đưa rất nhẹ và có một vệt sáng quét qua. Cắt hình bằng `clip-path` thay vì ghép hai khối
// — một khối thì nghiêng, đổ bóng và quét sáng đều ăn theo, không bao giờ lệch nhau.
const PAGE_CSS = `
@keyframes spk-bar { 0%, 100% { transform: scaleY(.3); } 50% { transform: scaleY(1); } }
.spk-bar { animation: spk-bar 1.15s ease-in-out infinite; transform-origin: center; }

@keyframes spk-tag-sway { 0%, 100% { transform: rotate(-7deg); } 50% { transform: rotate(-4deg); } }
@keyframes spk-tag-sheen { 0% { transform: translateX(-140%) skewX(-18deg); } 55%, 100% { transform: translateX(320%) skewX(-18deg); } }
@keyframes spk-tag-in { from { opacity: 0; transform: rotate(-16deg) translateX(-10px); } to { opacity: 1; transform: rotate(-7deg); } }
.spk-tag {
  position: relative;
  overflow: hidden;
  transform-origin: left center;
  /* Bo góc vẫn ăn: clip-path chỉ cắt thêm phần mũi nhọn bên trái, còn hai góc phải do
     border-radius lo. Ghép được vì đường cắt nằm hẳn bên trong vùng đã bo. */
  border-radius: 11px;
  clip-path: polygon(0% 50%, 19px 0%, 100% 0%, 100% 100%, 19px 100%);
  animation: spk-tag-in .45s ease-out both, spk-tag-sway 4.5s ease-in-out .45s infinite;
}
.spk-tag-sheen {
  position: absolute; top: 0; bottom: 0; left: 0; width: 34%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.45), transparent);
  animation: spk-tag-sheen 3.8s ease-in-out 1s infinite;
}
@media (prefers-reduced-motion: reduce) {
  .spk-bar, .spk-tag, .spk-tag-sheen { animation: none; }
  .spk-tag { transform: rotate(-7deg); }
  .spk-tag-sheen { display: none; }
}
`;


// ── Khoá tạm thời trong lúc dựng lại Speaking ──
// Danh sách tài khoản được phép nằm ở backend (app/utils/speaking_gate.py), không nhúng
// vào gói JS: đổi người test không phải dựng lại giao diện, và không sửa localStorage mà
// vào được. Hai endpoint tài liệu Speaking cũng tự chặn, đây chỉ là phần hiển thị.
const SpeakingMaintenance = ({ onBack }) => (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#eb7e37]/10 flex items-center justify-center mx-auto mb-5">
                <Wrench className="text-[#eb7e37]" size={30} strokeWidth={2} />
            </div>
            <h2 className="text-2xl font-bold text-[#2b5356] mb-3">Đang cập nhật tính năng</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
                Phần Speaking đang được nâng cấp. Chúng tôi sẽ mở lại ngay khi hoàn tất,
                các kỹ năng Listening, Reading và Writing vẫn dùng bình thường.
            </p>
            <button
                onClick={onBack}
                className="px-6 py-2.5 rounded-lg bg-[#0096b1] text-white font-semibold hover:bg-[#007a90] transition-colors"
            >
                Quay lại trang chủ
            </button>
        </div>
    </div>
);

const Speaking_Fe = () => {
    const navigate = useNavigate();
    const [section, setSection] = useState('mock');
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gate, setGate] = useState('checking');   // checking | open | locked
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const topicsPerPage = 6;

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                // Ask first: while Speaking is being rebuilt only the testers get in, and
                // the materials call below would 503 for everyone else anyway.
                const gateRes = await fetch(`${API_BASE}/student/speaking/access`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const gateData = await gateRes.json().catch(() => ({}));
                if (!gateRes.ok || !gateData.allowed) {
                    setGate('locked');
                    setLoading(false);
                    return;
                }
                setGate('open');

                // Always fetch Part 1 only (Speaking Forecast)
                const materialsResponse = await fetch(`${API_BASE}/student/speaking/materials?part=part1`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (materialsResponse.ok) {
                    const data = await materialsResponse.json();
                    const formatted = data.map(m => ({
                        id: m.material_id,
                        title: m.title || 'Untitled',
                        part_type: m.part_type,
                        pdf_url: toAbsoluteUrl(m.pdf_url),
                        created_at: m.created_at || new Date().toISOString(),
                        has_access: m.has_access
                    }));
                    setMaterials(formatted);
                } else if (materialsResponse.status === 401) {
                    navigate('/login');
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setMaterials([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate]);
    const [sortOrder, setSortOrder] = useState('alphabet');

    const filteredTopics = materials
        .filter(test => test.has_access)
        .filter(test => test.title.toLowerCase().includes(searchQuery.toLowerCase()))
        .sort((a, b) => {
            switch (sortOrder) {
                case 'alphabet':
                    return a.title.localeCompare(b.title);
                case 'latest':
                    return new Date(b.created_at) - new Date(a.created_at);
                case 'oldest':
                    return new Date(a.created_at) - new Date(b.created_at);
                default:
                    return a.title.localeCompare(b.title);
            }
        });

    const indexOfLastTopic = currentPage * topicsPerPage;
    const indexOfFirstTopic = indexOfLastTopic - topicsPerPage;
    const currentTopics = filteredTopics.slice(indexOfFirstTopic, indexOfLastTopic);
    const totalPages = Math.max(1, Math.ceil(filteredTopics.length / topicsPerPage));

    /**
     * Thẻ tài liệu. Trước đây là khối `p-8` với tiêu đề 2xl, một chip xám lặp lại đúng
     * chữ "Speaking Forecast" ở mọi thẻ, và bóng đổ nhảy hẳn một cấp khi rê chuột — ba
     * thẻ đã chiếm hết màn hình mà thông tin thật chỉ có mỗi cái tên.
     * Giờ: nhãn loại nhỏ ở trên, tên tài liệu là thứ to nhất, nút nằm đáy thẻ nên các
     * thẻ trong hàng thẳng chân nhau dù tên dài ngắn khác nhau.
     */
    const renderTopicCard = (m) => (
        <article key={m.id}
                 className="group flex flex-col rounded-xl border border-gray-200 bg-white p-5
                            transition-colors hover:border-[#0096b1]/60">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#0096b1]">
                Speaking Forecast
            </p>
            <h3 className="mt-2 text-[17px] font-bold leading-snug text-[#2b5356] break-words">
                {m.title}
            </h3>
            <div className="mt-auto pt-5">
                <button
                    disabled={!m.has_access}
                    onClick={() => {
                        if (m.has_access) {
                            navigate(`/speaking_test_room`, {
                                state: { title: m.title, pdfUrl: m.pdf_url, partType: m.part_type },
                            });
                        }
                    }}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5
                                text-sm font-bold transition-colors ${m.has_access
                            ? 'bg-[#0096b1] text-white hover:bg-[#007a90]'
                            : 'cursor-not-allowed bg-gray-100 text-gray-400'}`}
                >
                    {m.has_access ? <Play className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    {m.has_access ? 'Xem tài liệu' : 'Cần VIP'}
                </button>
            </div>
        </article>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 className="animate-spin text-[#0096b1]" size={30} />
            </div>
        );
    }

    if (gate === 'locked') {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <SpeakingMaintenance onBack={() => navigate('/')} />
            </div>
        );
    }

    const active = SECTIONS.find((sec) => sec.key === section) || SECTIONS[0];
    /**
     * Nội dung của từng nhánh. Ba con số ở `meta` là số thật lấy từ cách dựng đề bên máy
     * chủ (app/utils/speaking_assemble.py), không phải chữ cho đẹp: Part 1 ra 3–4 câu,
     * Part 2 là cue card + 1 câu nối, Part 3 là 6 câu ngân hàng + 1 câu AI hỏi thêm.
     */
    const HERO = {
        mock: {
            tag: 'MOCK TEST',
            note: 'Vừa ra mắt · bản đầy đủ Part 1–2–3',
            title: 'Thi Speaking cùng giám khảo AI',
            desc: 'Trả lời bằng giọng nói theo đúng trình tự Part 1 – 2 – 3, có bấm giờ như phòng '
                + 'thi thật, hoặc luyện tập thong thả với gợi ý và từ vựng cho từng câu.',
            cta: 'Bắt đầu thi thử', to: '/speaking_test_setup',
            caption: 'Giám khảo sẵn sàng',
            meta: ['3 parts', 'Khoảng 15 phút', 'Miễn phí 1 lượt mỗi ngày'],
        },
        forecast: {
            tag: 'FORECAST',
            note: 'Bộ đề dự đoán theo tháng thi',
            title: 'Luyện theo chủ đề dự đoán',
            desc: 'Chọn đúng chủ đề bạn muốn luyện và hỏi hết mọi câu trong chủ đề đó.',
            cta: 'Xem đề dự đoán', to: '/speaking_forecast',
            caption: 'Đề dự đoán đã sẵn',
            meta: ['Luyện từng Part riêng', 'Không bấm giờ', 'Làm lại bao nhiêu lần cũng được'],
        },
        lessons: {
            tag: 'PRONUNCIATION',
            note: '',
            title: 'Học lý thuyết phát âm và luyện tập phát âm với AI',
            desc: '',
            cta: 'Vào bài học phát âm', to: '/speaking_lessons',
            caption: 'Sẵn sàng luyện âm',
            meta: ['Có lý thuyết từng bài', 'AI chấm âm và trọng âm', 'Bộ luyện tập riêng của bạn'],
        },
    }[section];

    return (
        <div className="min-h-screen bg-gray-50">
            <style>{PAGE_CSS}</style>
            <Navbar />

            {/* Cột nội dung hẹp lại (1024px thay vì 1280px): ở 1280px thẻ giới thiệu thừa ra
                một mảng trống giữa cột chữ và giám khảo, vì chữ đã chặn bề ngang cho dễ đọc
                nên không có gì lấp vào đó. Hẹp lại thì hai bên tự chạm nhau. Đổi ở đây chứ
                không riêng cái thẻ, để breadcrumb, tab, thẻ giới thiệu và ba thẻ bước vẫn
                thẳng một mép. */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 pb-12">
                <nav aria-label="Breadcrumb" className="text-sm">
                    <ol className="flex items-center gap-2 text-gray-500">
                        <li><Link to="/" className="hover:text-[#0096b1]">Trang chủ</Link></li>
                        <li aria-hidden="true" className="text-gray-300">/</li>
                        <li className="font-medium text-[#2b5356]">Speaking</li>
                    </ol>
                </nav>

                {/* Tiêu đề trang. Trước đây trang không có tiêu đề nào: mở lên là bốn nút rồi
                    tới một khối nền tối, không có chỗ nào nói đây là trang gì. */}
                <header className="mt-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-[32px] sm:text-[44px] font-extrabold tracking-tight text-[#2b5356]">
                            Speaking
                        </h1>
                        {/* Speaking vừa mở lại sau đợt dựng lại; nhãn này gỡ đi khi hết mới. */}
                        <span className="spk-tag -ml-1 sm:ml-0 inline-flex items-center gap-2.5 bg-[#d97a3c]
                                         py-2.5 pl-7 pr-5 text-[13px] font-extrabold uppercase
                                         tracking-[0.12em] text-white">
                            <span aria-hidden="true"
                                  className="h-2 w-2 shrink-0 rounded-full bg-white/85" />
                            Chức năng mới
                            <span aria-hidden="true" className="spk-tag-sheen" />
                        </span>
                    </div>
                </header>

                {/* Bốn nhánh của Speaking, mỗi cái một thẻ lớn (user chốt 14/09: "làm 4 mục
                    này to và rõ hơn, thật to"). Bản trước là một dòng chữ xám liệt kê cả bốn
                    cộng một hàng tab mảnh: đúng thứ người ta lướt qua mà không đọc. Giờ mỗi
                    nhánh tự chiếm một ô, có icon, tên và một dòng nói nó làm gì. */}
                <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {SECTIONS.map((sec) => {
                        const on = section === sec.key;
                        return (
                            <button key={sec.key} onClick={() => setSection(sec.key)}
                                    aria-current={on ? 'page' : undefined}
                                    className={`rounded-2xl border-2 p-5 text-left transition-colors ${on
                                            ? 'border-[#0096b1] bg-[#0096b1]/8'
                                            : 'border-gray-200 bg-white hover:border-[#0096b1]/50'}`}>
                                <sec.icon className={`h-7 w-7 ${on ? 'text-[#0096b1]' : 'text-[#2b5356]'}`} />
                                <p className={`mt-3 text-xl font-extrabold leading-tight ${
                                    on ? 'text-[#0096b1]' : 'text-[#2b5356]'}`}>
                                    {sec.label}
                                </p>
                                <p className="mt-1.5 text-[15px] leading-snug text-gray-500">{sec.blurb}</p>
                            </button>
                        );
                    })}
                </div>

                {HERO && (
                    <>
                        {/* Sân khấu giới thiệu. Nền xanh đậm thương hiệu, chữ trắng, một nút cam
                            duy nhất, cả dải chỉ để dẫn tới đúng một việc. Bên phải là giám khảo:
                            cùng hình ảnh sẽ gặp lại trong phòng thi, nên vào phòng không bỡ ngỡ. */}
                        <section className="relative mt-6 overflow-hidden rounded-[22px] text-white"
                                 style={{ background: '#2b5356' }}>
                            <div aria-hidden="true"
                                 className="pointer-events-none absolute -right-[6%] top-1/2 hidden lg:block"
                                 style={{ width: 440, height: 440, marginTop: -220, borderRadius: '50%',
                                          background: 'rgba(255,255,255,0.06)' }} />
                            <div className="relative flex flex-col gap-6 p-6 sm:p-7 lg:flex-row lg:items-center">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2.5">
                                        <span className="rounded-full bg-white/15 px-3 py-1 text-[13px] font-bold">
                                            Speaking
                                        </span>
                                        <span className="rounded-full px-3 py-1 text-[12px] font-extrabold
                                                         tracking-[0.1em] text-white"
                                              style={{ background: 'rgba(0,0,0,0.22)' }}>
                                            {HERO.tag}
                                        </span>
                                        {HERO.note && (
                                            <span className="text-[13px] font-semibold text-white/70">{HERO.note}</span>
                                        )}
                                    </div>

                                    {/* Cột chữ trải hết phần còn lại để giám khảo đứng sát mép phải,
                                        không chừa một mảng trống giữa hai bên. Tiêu đề và mô tả vẫn tự
                                        chặn bề ngang riêng, không kéo dài thành dòng khó đọc. */}
                                    <h2 className="mt-4 max-w-2xl text-[24px] sm:text-[30px] font-extrabold
                                                   leading-[1.2] tracking-tight">
                                        {HERO.title}
                                    </h2>
                                    {HERO.desc && (
                                        <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-white/80">
                                            {HERO.desc}
                                        </p>
                                    )}

                                    <button onClick={() => navigate(HERO.to)}
                                            className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl
                                                       bg-[#eb7e37] px-6 py-3 text-[15px] font-bold text-white
                                                       transition-colors hover:bg-[#d86f2b]">
                                        <active.icon className="h-5 w-5" />
                                        {HERO.cta}
                                    </button>

                                    <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2
                                                    text-[13px] font-semibold text-white/70">
                                        {HERO.meta.map((m, i) => (
                                            <React.Fragment key={m}>
                                                {i > 0 && <span aria-hidden="true" className="text-white/30">•</span>}
                                                <span>{m}</span>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex shrink-0 flex-col items-center gap-3 lg:pr-4">
                                    <div className="flex h-[132px] w-[132px] items-center justify-center
                                                    rounded-[24px] bg-white">
                                        <img src="/img/logo-ielts.png" alt="Giám khảo AI"
                                             className="h-[112px] w-[112px] object-contain" />
                                    </div>
                                    {/* Cột sóng: chỉ để nói "đây là phần NÓI", không đo gì cả —
                                        nên nhịp chạy đều, không giả vờ theo tiếng người dùng. */}
                                    <div className="flex h-7 items-center gap-[3px]">
                                        {Array.from({ length: 18 }, (_, i) => (
                                            <span key={i} className="spk-bar block w-[3px] rounded-full bg-white/85"
                                                  style={{ height: Math.round(28 * (0.3 + 0.7 * Math.abs(Math.sin((i + 1) * 1.7)))),
                                                           animationDelay: `${(i * 0.07).toFixed(2)}s` }} />
                                        ))}
                                    </div>
                                    <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/75">
                                        {HERO.caption}
                                    </p>
                                </div>
                            </div>
                        </section>

                    </>
                )}

                {section === 'documents' && (
                    <section className="mt-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px]
                                                   -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Tìm theo tên tài liệu"
                                    className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4
                                               text-sm outline-none transition-colors
                                               focus:border-[#0096b1] focus:ring-1 focus:ring-[#0096b1]"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <select
                                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium
                                           text-[#2b5356] outline-none transition-colors
                                           focus:border-[#0096b1] focus:ring-1 focus:ring-[#0096b1]"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="alphabet">Theo bảng chữ cái</option>
                                <option value="latest">Mới nhất</option>
                                <option value="oldest">Cũ nhất</option>
                            </select>
                        </div>

                        {currentTopics.length === 0 ? (
                            <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-white
                                            px-6 py-16 text-center">
                                <BookOpen className="mx-auto mb-3 text-gray-300" size={30} />
                                <p className="font-semibold text-[#2b5356]">Chưa có tài liệu nào</p>
                                <p className="mt-1 text-sm text-gray-500">
                                    {searchQuery ? 'Không có tài liệu nào khớp với từ khoá bạn tìm.'
                                                 : 'Tài liệu Speaking sẽ xuất hiện ở đây khi được thêm.'}
                                </p>
                            </div>
                        ) : (
                            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {currentTopics.map(renderTopicCard)}
                            </div>
                        )}

                        {totalPages > 1 && (
                            <nav className="mt-8 flex items-center justify-center gap-4">
                                <button onClick={() => setCurrentPage((n) => Math.max(n - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="inline-flex items-center gap-1 rounded-xl border-2 border-gray-200
                                                   px-4 py-2 text-sm font-bold text-[#2b5356] transition-colors
                                                   hover:border-gray-300 disabled:opacity-40">
                                    <ChevronLeft className="h-4 w-4" /> Trước
                                </button>
                                <span className="text-sm font-semibold tabular-nums text-gray-500">
                                    Trang {currentPage} / {totalPages}
                                </span>
                                <button onClick={() => setCurrentPage((n) => Math.min(n + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="inline-flex items-center gap-1 rounded-xl border-2 border-gray-200
                                                   px-4 py-2 text-sm font-bold text-[#2b5356] transition-colors
                                                   hover:border-gray-300 disabled:opacity-40">
                                    Sau <ChevronRight className="h-4 w-4" />
                                </button>
                            </nav>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
};

export default Speaking_Fe;
